/**
 * SpectatorShareDialog — Create and display a live share link with QR code.
 *
 * Presenter clicks "Share Live" in the workspace header, gets a share URL + QR code.
 * Audience scans QR to watch the session in real-time at /live/:shareId.
 *
 * State is NOT stored in localStorage — the backend (Redis) is the source of truth.
 * On dialog open, validate the last known share_id; fetch alias via reverse lookup.
 */
import { useState, useCallback, useRef, useEffect } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { userAPI } from '@/lib/api/client'
import { getApiErrorMessage } from '@/lib/api/errors'
import {
  Copy,
  Check,
  QrCode,
  Loader2,
  X,
  Radio,
  Download,
  ClipboardCopy,
  Link2,
} from 'lucide-react'

interface SpectatorShareDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ShareLink {
  share_id: string
  url: string
  expires_at: string
  duration_hours: number
}

interface AliasInfo {
  alias: string
  url: string
}

function formatRemaining(seconds: number): string {
  if (seconds <= 0) return 'Expired'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}m remaining`
  if (m > 0) return `${m}m remaining`
  return `${seconds}s remaining`
}

export function SpectatorShareDialog({ open, onOpenChange }: SpectatorShareDialogProps) {
  const [link, setLink] = useState<ShareLink | null>(null)
  const [loading, setLoading] = useState(false)
  const [restoring, setRestoring] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [copiedQr, setCopiedQr] = useState(false)
  const [duration, setDuration] = useState(4)
  const [includeExisting, setIncludeExisting] = useState(true)

  // Alias state
  const [aliasInput, setAliasInput] = useState('')
  const [activeAlias, setActiveAlias] = useState<AliasInfo | null>(null)
  const [aliasLoading, setAliasLoading] = useState(false)
  const [aliasError, setAliasError] = useState<string | null>(null)

  // Countdown
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null)
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const qrRef = useRef<HTMLDivElement>(null)
  const lastShareIdRef = useRef<string | null>(null)

  // Start countdown timer
  const startCountdown = useCallback((seconds: number) => {
    if (countdownRef.current) clearInterval(countdownRef.current)
    setRemainingSeconds(seconds)
    countdownRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev === null || prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current)
    }
  }, [])

  // On dialog open: check for active session from backend
  useEffect(() => {
    if (!open) return

    setRestoring(true)
    ;(async () => {
      try {
        const resp = await userAPI.getActiveSpectatorSession()
        const data = resp.data
        if (!data.active) {
          setLink(null)
          lastShareIdRef.current = null
          setActiveAlias(null)
          return
        }

        const restored: ShareLink = {
          share_id: data.share_id,
          url: data.url,
          expires_at: data.expires_at,
          duration_hours: Math.ceil((data.remaining_seconds || 0) / 3600) || 1,
        }
        setLink(restored)
        lastShareIdRef.current = data.share_id
        startCountdown(data.remaining_seconds || 0)

        if (data.alias) {
          setActiveAlias({ alias: data.alias, url: data.alias_url })
        } else {
          setActiveAlias(null)
        }
      } catch {
        setLink(null)
        lastShareIdRef.current = null
        setActiveAlias(null)
      } finally {
        setRestoring(false)
      }
    })()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const createLink = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await userAPI.createSpectatorLink(duration, includeExisting)
      const newLink = resp.data
      setLink(newLink)
      lastShareIdRef.current = newLink.share_id
      startCountdown(duration * 3600)
      setActiveAlias(null)
      setAliasInput('')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to create share link'))
    } finally {
      setLoading(false)
    }
  }, [duration, includeExisting, startCountdown])

  const revokeLink = useCallback(async () => {
    if (!link) return
    try {
      await userAPI.revokeSpectatorLink(link.share_id)
    } catch {
      /* ignore — may already be expired */
    }
    setLink(null)
    lastShareIdRef.current = null
    setActiveAlias(null)
    setAliasInput('')
    setAliasError(null)
    setRemainingSeconds(null)
    if (countdownRef.current) clearInterval(countdownRef.current)
  }, [link])

  const setAlias = useCallback(async () => {
    if (!link || !aliasInput.trim()) return
    const clean = aliasInput.trim().toLowerCase()
    if (!/^[a-z0-9]+$/.test(clean) || clean.length > 30) {
      setAliasError('Letters and numbers only, max 30 chars')
      return
    }
    setAliasLoading(true)
    setAliasError(null)
    try {
      const resp = await userAPI.setSpectatorAlias(clean, link.share_id)
      setActiveAlias({ alias: clean, url: resp.data.url })
    } catch (err) {
      setAliasError(getApiErrorMessage(err, 'Failed to set alias'))
    } finally {
      setAliasLoading(false)
    }
  }, [link, aliasInput])

  const copyUrl = useCallback(() => {
    if (!link) return
    navigator.clipboard.writeText(activeAlias?.url || link.url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [link, activeAlias])

  const handleClose = (val: boolean) => {
    if (!val) {
      setError(null)
      setCopied(false)
      setCopiedQr(false)
    }
    onOpenChange(val)
  }

  const getQrCanvas = (): HTMLCanvasElement | null => qrRef.current?.querySelector('canvas') ?? null

  const downloadQr = useCallback(() => {
    const canvas = getQrCanvas()
    if (!canvas) return
    const url = canvas.toDataURL('image/png')
    const a = document.createElement('a')
    a.href = url
    a.download = `ecosemantic-live-${activeAlias?.alias || link?.share_id || 'qr'}.png`
    a.click()
  }, [link, activeAlias])

  const copyQrToClipboard = useCallback(async () => {
    const canvas = getQrCanvas()
    if (!canvas) return
    try {
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/png'))
      if (blob) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
        setCopiedQr(true)
        setTimeout(() => setCopiedQr(false), 2000)
      }
    } catch {
      downloadQr()
    }
  }, [downloadQr])

  const displayUrl = activeAlias?.url || link?.url || ''

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radio className="w-5 h-5 text-red-500" />
            Share Live Session
          </DialogTitle>
          <DialogDescription>
            Create a link for spectators to watch your workspace in real-time.
          </DialogDescription>
        </DialogHeader>

        {restoring ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : !link ? (
          /* ---- CREATE LINK VIEW ---- */
          <div className="space-y-4 pt-2">
            <div className="flex items-center gap-3">
              <label className="text-sm text-muted-foreground whitespace-nowrap">Duration:</label>
              <div className="flex gap-1.5">
                {[1, 2, 4, 8].map((h) => (
                  <Button
                    key={h}
                    variant={duration === h ? 'default' : 'outline'}
                    size="sm"
                    className="h-8 px-3"
                    onClick={() => setDuration(h)}
                  >
                    {h}h
                  </Button>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <label className="text-sm text-muted-foreground whitespace-nowrap pt-0.5">
                Visibility:
              </label>
              <div className="flex flex-col gap-1.5">
                <button
                  onClick={() => setIncludeExisting(true)}
                  className={`text-left text-sm px-3 py-1.5 rounded-md border transition-colors ${
                    includeExisting
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-muted bg-transparent text-muted-foreground hover:border-muted-foreground/30'
                  }`}
                >
                  Include existing progress
                </button>
                <button
                  onClick={() => setIncludeExisting(false)}
                  className={`text-left text-sm px-3 py-1.5 rounded-md border transition-colors ${
                    !includeExisting
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-muted bg-transparent text-muted-foreground hover:border-muted-foreground/30'
                  }`}
                >
                  Start fresh — wait for new project
                </button>
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button onClick={createLink} disabled={loading} className="w-full gap-2">
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <QrCode className="w-4 h-4" />
              )}
              Generate Share Link
            </Button>
          </div>
        ) : (
          /* ---- LINK ACTIVE VIEW ---- */
          <div className="space-y-4 pt-2">
            {/* QR Code */}
            <div className="flex justify-center">
              <div ref={qrRef} className="bg-white p-4 rounded-xl">
                <QRCodeCanvas value={displayUrl} size={200} level="M" includeMargin />
              </div>
            </div>

            {/* QR actions */}
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5"
                onClick={copyQrToClipboard}
              >
                {copiedQr ? (
                  <Check className="w-3.5 h-3.5 text-green-500" />
                ) : (
                  <ClipboardCopy className="w-3.5 h-3.5" />
                )}
                {copiedQr ? 'Copied!' : 'Copy Image'}
              </Button>
              <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={downloadQr}>
                <Download className="w-3.5 h-3.5" />
                Download PNG
              </Button>
            </div>

            {/* URL display — scrollable, selectable, full width */}
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0 bg-muted rounded-md px-3 py-2 overflow-x-auto">
                <span className="text-sm font-mono whitespace-nowrap select-all">{displayUrl}</span>
              </div>
              <Button variant="outline" size="icon" className="shrink-0 h-9 w-9" onClick={copyUrl}>
                {copied ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>

            {/* Stable Alias */}
            {!activeAlias ? (
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground">
                  Stable alias (for QR codes in slides):
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={aliasInput}
                    onChange={(e) =>
                      setAliasInput(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && aliasInput.trim()) setAlias()
                    }}
                    placeholder="e.g. eth"
                    className="flex-1 min-w-0 bg-muted rounded-md px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                    maxLength={30}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-1.5 h-8"
                    onClick={setAlias}
                    disabled={aliasLoading || !aliasInput.trim()}
                  >
                    {aliasLoading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Link2 className="w-3.5 h-3.5" />
                    )}
                    Set
                  </Button>
                </div>
                {aliasError && <p className="text-xs text-destructive">{aliasError}</p>}
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Link2 className="w-3.5 h-3.5 text-green-500" />
                  <span>
                    Stable alias{' '}
                    <span className="font-mono text-foreground">{activeAlias.alias}</span> active
                  </span>
                </div>
              </div>
            )}

            {/* Live countdown */}
            <p className="text-xs text-muted-foreground text-center">
              {remainingSeconds !== null && remainingSeconds > 0
                ? formatRemaining(remainingSeconds)
                : remainingSeconds === 0
                  ? 'Session expired'
                  : `${link.duration_hours}h session`}
            </p>

            {/* Revoke */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-destructive hover:text-destructive gap-2"
              onClick={revokeLink}
            >
              <X className="w-3.5 h-3.5" />
              Revoke Link
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
